import React, { useState, useEffect, useContext, useCallback } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import ImageWithBasePath from "../../core/common/imageWithBasePath";
import DefaultEditor from "react-simple-wysiwyg";
import JSZip from "jszip";
import {
  DocumentService,
  Document,
  DocumentAttachment,
} from "../../feature-module/crm/documents/document.service";
import {
  TenantUserService,
  TenantUser,
} from "../../feature-module/crm/leads/tenantUser.service";
import {
  showSuccessAlert,
  showErrorAlert,
  showDeleteConfirmationAlert,
} from "../../utils/alertUtils";
import { formatCustomDateTime } from "../../utils/dateUtils";
import { AuthContext } from "../../context/authcontext";
import male from "../../assets/male.png";
import { useTabVisibility } from "../../hooks/useTabVisibility";
import { Button } from "react-bootstrap";
import AttachmentComponent, { Attachment } from "../AttachmentComponent";
import { getLifecycleTypeName } from "../../utils/lifecycleUtils";

const getDocumentTypeOptions = () => [
  { value: "Contract", label: getLifecycleTypeName("CONTRACT") },
  { value: "Proposal", label: getLifecycleTypeName("PROPOSAL") },
  { value: "Quote", label: "Quote" },
];

interface FilesProps {
  tenant_id?: number | string | null;
  related_entity_id?: number | string | null;
  related_entity_name?: string;
  refreshTrigger?: number;
  onActivityUpdate?: () => void;
}

const Files: React.FC<FilesProps> = ({
  tenant_id,
  related_entity_id,
  related_entity_name,
  refreshTrigger,
  onActivityUpdate,
}) => {
  const { auth } = useContext(AuthContext);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [tenantUsers, setTenantUsers] = useState<TenantUser[]>([]);

  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [newDocument, setNewDocument] = useState({
    title: "",
    content: "",
    document_type: "PDF",
    assigned_user_id: null as number | null,
  });
  const [documentAttachments, setDocumentAttachments] = useState<File[]>([]);

  const [editingDocument, setEditingDocument] = useState<number | null>(null);
  const [editDocumentData, setEditDocumentData] = useState({
    title: "",
    content: "",
    document_type: "PDF",
    assigned_user_id: null as number | null,
  });
  const [editDocumentAttachments, setEditDocumentAttachments] = useState<
    File[]
  >([]);
  const [attachmentsToDelete, setAttachmentsToDelete] = useState<number[]>([]);

  const [documentAttachmentsList, setDocumentAttachmentsList] = useState<{
    [documentId: number]: DocumentAttachment[];
  }>({});
  const [savingDocument, setSavingDocument] = useState(false);
  const [updatingDocument, setUpdatingDocument] = useState(false);
  const [downloadingDocument, setDownloadingDocument] = useState<number | null>(
    null
  );
  const [downloadingAttachment, setDownloadingAttachment] = useState<number | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  const currentTenantId = Number(
    tenant_id || localStorage.getItem("selectedTenantId")
  );
  const currentRelatedEntityId = Number(related_entity_id);
  const currentUserId = auth?.user_id;

  // Use tab visibility hook for lazy loading
  const { isVisible, hasBeenActivated } = useTabVisibility({
    tabId: 'files',
    onTabActivated: () => {
      if (!dataLoaded && currentTenantId && currentRelatedEntityId) {
        loadDocuments();
        loadTenantUsers();
        setDataLoaded(true);
      }
    }
  });

  // Only load data when tab is activated and data hasn't been loaded yet
  useEffect(() => {
    if (hasBeenActivated && !dataLoaded && currentTenantId && currentRelatedEntityId) {
      loadDocuments();
      loadTenantUsers();
      setDataLoaded(true);
    }
  }, [hasBeenActivated, dataLoaded, currentTenantId, currentRelatedEntityId]);

  // Handle refresh trigger
  useEffect(() => {
    if (dataLoaded && refreshTrigger && currentTenantId && currentRelatedEntityId) {
      loadDocuments();
    }
  }, [refreshTrigger, dataLoaded, currentTenantId, currentRelatedEntityId]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const response = await DocumentService.getDocuments(
        currentTenantId,
        currentRelatedEntityId
      );
      setDocuments(response.results || []);

      response.results?.forEach((document) => {
        if (document.document_id) {
          loadAttachmentsForDocument(document.document_id);
        }
      });
    } catch (error) {
      console.error("Error loading documents:", error);
      showErrorAlert("Failed to load documents");
    } finally {
      setLoading(false);
    }
  };

  const loadAttachmentsForDocument = async (documentId: number) => {
    try {
      const attachments = await DocumentService.getDocumentAttachments(
        documentId
      );
      setDocumentAttachmentsList((prev) => ({
        ...prev,
        [documentId]: attachments,
      }));
    } catch (error) {
      console.error(
        `Error loading attachments for document ${documentId}:`,
        error
      );
    }
  };

  const loadTenantUsers = async () => {
    try {
      const users = await TenantUserService.getAllTenantUsers();
      setTenantUsers(users);
    } catch (error) {
      console.error("Error loading tenant users:", error);
    }
  };

  const handleAddDocument = async () => {
    if (!newDocument.title.trim() || !newDocument.content.trim()) {
      showErrorAlert("Please fill in both title and content");
      return;
    }

    if (!currentUserId) {
      showErrorAlert("User not authenticated");
      return;
    }

    try {
      setSavingDocument(true);
      const documentData = {
        title: newDocument.title,
        content: newDocument.content,
        document_type: newDocument.document_type,
        assigned_user_id: newDocument.assigned_user_id || undefined,
        related_entity_id: currentRelatedEntityId,
        related_entity_name: related_entity_name,
        created_by: currentUserId,
        tenant_id: currentTenantId,
      };

      await DocumentService.createDocument(
        documentData,
        currentTenantId,
        documentAttachments
      );

      setNewDocument({
        title: "",
        content: "",
        document_type: "PDF",
        assigned_user_id: null,
      });
      setDocumentAttachments([]);
      setShowAddDocumentModal(false);

      loadDocuments();
      showSuccessAlert("Document added successfully");

      if (onActivityUpdate) {
        onActivityUpdate();
      }
    } catch (error) {
      console.error("Error adding document:", error);
      showErrorAlert("Failed to add document");
    } finally {
      setSavingDocument(false);
    }
  };

  const handleDeleteDocument = async (documentId: number) => {
    try {
      const result = await showDeleteConfirmationAlert(
        "Are you sure?",
        "This will permanently delete the document and all its attachments.",
        "Yes, delete it!",
        "Cancel"
      );

      if (result.isConfirmed) {
        await DocumentService.deleteDocument(documentId, currentTenantId);
        loadDocuments();
        showSuccessAlert("Document deleted successfully");

        if (onActivityUpdate) {
          onActivityUpdate();
        }
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      showErrorAlert("Failed to delete document");
    }
  };

  const handleFileSelection = (files: FileList | null) => {
    if (!files) return;

    const allowedTypes = [
      ".jpg",
      ".jpeg",
      ".png",
      ".pdf",
      ".doc",
      ".docx",
      ".xls",
      ".xlsx",
    ];
    const validFiles: File[] = [];

    Array.from(files).forEach((file) => {
      const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();
      if (allowedTypes.includes(fileExtension)) {
        validFiles.push(file);
      } else {
        showErrorAlert(
          `File "${file.name}" is not allowed. Only ${allowedTypes.join(
            ", "
          )} files are permitted.`
        );
      }
    });

    setDocumentAttachments((prev) => [...prev, ...validFiles]);
  };

  const removeAttachment = (index: number) => {
    setDocumentAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const formatDateTime = (dateString: string) => {
    return formatCustomDateTime(dateString);
  };

  const getUserName = (createdBy: any) => {
    if (typeof createdBy === "object" && createdBy?.full_name) {
      return createdBy?.full_name;
    }
    return "Unknown User";
  };

  const getAssignedUserName = (assignedUserId: number | null) => {
    if (!assignedUserId) return "Not assigned";
    const user = tenantUsers.find((u) => u.user_id === assignedUserId);
    return user
      ? `${user.user_first_name} ${user.user_last_name}`
      : "Unknown User";
  };

  const downloadDocumentAsZip = async (document: Document) => {
    try {
      const attachments = documentAttachmentsList[document.document_id!] || [];

      if (attachments.length === 0) {
        showErrorAlert("No attachments to download");
        return;
      }

      setDownloadingDocument(document.document_id!);

      const zip = new JSZip();

      const downloadPromises = attachments.map(async (attachment, index) => {
        try {
          const response = await fetch(attachment.attachment_url);
          if (!response.ok) {
            throw new Error(`Failed to fetch ${attachment.title}`);
          }
          const blob = await response.blob();

          let fileName = attachment.title;
          let counter = 1;
          while (zip.file(fileName)) {
            const parts = attachment.title.split(".");
            if (parts.length > 1) {
              const extension = parts.pop();
              const nameWithoutExt = parts.join(".");
              fileName = `${nameWithoutExt}_${counter}.${extension}`;
            } else {
              fileName = `${attachment.title}_${counter}`;
            }
            counter++;
          }

          zip.file(fileName, blob);
        } catch (error) {
          console.error(`Failed to download ${attachment.title}:`, error);
        }
      });

      await Promise.all(downloadPromises);

      const zipBlob = await zip.generateAsync({ type: "blob" });

      const downloadUrl = window.URL.createObjectURL(zipBlob);
      const link = window.document.createElement("a");
      link.href = downloadUrl;
      link.download = `${document.title.replace(
        /[^a-z0-9]/gi,
        "_"
      )}_attachments.zip`;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);

      window.URL.revokeObjectURL(downloadUrl);

      showSuccessAlert("Download completed successfully");
    } catch (error) {
      console.error("Error creating zip file:", error);
      showErrorAlert("Failed to download document attachments");
    } finally {
      setDownloadingDocument(null);
    }
  };

  const getDocumentIcon = (documentType: string) => {
    switch (documentType?.toLowerCase()) {
      case "pdf":
        return "assets/img/icons/pdf-02.svg";
      case "word":
        return "assets/img/icons/word.png";
      case "excel":
        return "assets/img/icons/xls.svg";
      case "picture":
        return "assets/img/icons/image.svg";
      default:
        return "assets/img/icons/folder.svg";
    }
  };

  const getStatusBadge = (documentType: string) => {
    switch (documentType?.toLowerCase()) {
      case "contract":
        return (
          <span className="badge badge-danger-light me-1">
            {getLifecycleTypeName("CONTRACT")}
          </span>
        );
      case "proposal":
        return (
          <span className="badge badge-danger-light me-1">
            {getLifecycleTypeName("PROPOSAL")}
          </span>
        );
      case "quote":
        return <span className="badge badge-purple-light me-1">Quote</span>;
      default:
        return (
          <span className="badge badge-secondary-light me-1">Document</span>
        );
    }
  };

  const getAttachmentIcon = (filename: string | null | undefined) => {
    if (!filename) return "assets/img/icons/file.svg";
    const extension = filename.split(".").pop()?.toLowerCase();
    switch (extension) {
      case "pdf":
        return "assets/img/icons/pdf.svg";
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return "assets/img/icons/image.svg";
      case "doc":
      case "docx":
        return "assets/img/icons/word.png";
      case "xls":
      case "xlsx":
        return "assets/img/icons/xls.svg";
      default:
        return "assets/img/icons/file.svg";
    }
  };

  const truncateFileName = (filename: string, maxLength: number) => {
    if (!filename || filename.length <= maxLength) return filename;

    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex === -1) {
      return `${filename.substring(0, maxLength - 3)}...`;
    }

    const extension = filename.substring(lastDotIndex);
    const nameWithoutExt = filename.substring(0, lastDotIndex);

    if (extension.length >= maxLength - 3) {
      return `${filename.substring(0, maxLength - 3)}...`;
    }

    const availableLength = maxLength - extension.length - 3;

    if (nameWithoutExt.length <= availableLength) {
      return filename;
    }

    return `${nameWithoutExt.substring(0, availableLength)}...${extension}`;
  };

  const downloadAttachment = async (url: string, filename: string, attachmentId: number) => {
    try {
      setDownloadingAttachment(attachmentId);
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement("a");
      link.href = downloadUrl;
      link.download = filename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Download failed:", error);

      window.open(url, "_blank");
    } finally {
      setDownloadingAttachment(null);
    }
  };

  return (
    <>
      <div className="tab-pane fade" id="files">
        <div className="card">
          <div className="card-header">
            <div className="d-flex align-items-center justify-content-between">
              <h4 className="fw-semibold">Files & Documents</h4>
              <Button className="btn btn-primary" onClick={() => setShowAddDocumentModal(true)}>
                <i className="ti ti-circle-plus me-1" />
                Add New Document
              </Button>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center p-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                {documents.length === 0 ? (
                  <div className="card border shadow-none mb-3">
                    <div className="card-body text-center py-5">
                      <p className="mb-0">
                        No documents found. Click{" "}
                        <span className="fw-semibold">"Create Document"</span>{" "}
                        to create your first document.
                      </p>
                    </div>
                  </div>
                ) : (
                  documents.map((document) => (
                    <div
                      key={document.document_id}
                      className="card border shadow-none mb-3"
                    >
                      <div className="card-body pb-0">
                        {editingDocument === document.document_id ? (
                          <div className="mb-3">
                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label className="form-label">Title</label>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={editDocumentData.title ?? ""}
                                  onChange={(e) =>
                                    setEditDocumentData({
                                      ...editDocumentData,
                                      title: e.target.value,
                                    })
                                  }
                                  placeholder="Document title"
                                />
                              </div>
                              <div className="col-md-6 mb-3">
                                <label className="form-label">
                                  Document Type
                                </label>
                                <Select
                                  className="select"
                                  options={getDocumentTypeOptions()}
                                  value={getDocumentTypeOptions().find(
                                    (option) =>
                                      option.value ===
                                      editDocumentData.document_type
                                  )}
                                  onChange={(selected) =>
                                    setEditDocumentData({
                                      ...editDocumentData,
                                      document_type: selected?.value || "PDF",
                                    })
                                  }
                                  classNamePrefix="react-select"
                                />
                              </div>
                              <div className="col-md-12 mb-3">
                                <label className="form-label">
                                  Assigned User
                                </label>
                                <Select
                                  className="select"
                                  options={tenantUsers.map((user) => ({
                                    value: user.user_id,
                                    label: `${user.user_first_name} ${user.user_last_name} (${user.email})`,
                                  }))}
                                  value={tenantUsers
                                    .map((user) => ({
                                      value: user.user_id,
                                      label: `${user.user_first_name} ${user.user_last_name} (${user.email})`,
                                    }))
                                    .find(
                                      (option) =>
                                        option.value ===
                                        editDocumentData.assigned_user_id
                                    )}
                                  onChange={(selected) =>
                                    setEditDocumentData({
                                      ...editDocumentData,
                                      assigned_user_id: selected?.value || null,
                                    })
                                  }
                                  classNamePrefix="react-select"
                                  placeholder="Select assigned user (optional)"
                                  isClearable
                                />
                              </div>
                              <div className="col-md-12 mb-3">
                                <label className="form-label">Content</label>
                                <DefaultEditor
                                  value={editDocumentData.content ?? ""}
                                  onChange={(e) =>
                                    setEditDocumentData({
                                      ...editDocumentData,
                                      content: e.target.value,
                                    })
                                  }
                                />
                              </div>
                              <div className="col-md-12 mb-3">
                                <label className="form-label">
                                  Attachments
                                </label>

                                {/* Existing Attachments */}
                                {documentAttachmentsList[
                                  document.document_id!
                                ] &&
                                  documentAttachmentsList[document.document_id!]
                                    .length > 0 && (
                                    <div className="mb-3">
                                      <h6 className="fw-medium mb-2">
                                        Current Attachments
                                      </h6>
                                      {documentAttachmentsList[
                                        document.document_id!
                                      ].map((attachment) => (
                                        <div
                                          key={
                                            attachment.document_attachment_id
                                          }
                                          className="upload-file upload-list"
                                        >
                                          <div className="d-flex align-items-center">
                                            <ImageWithBasePath
                                              src={getAttachmentIcon(
                                                attachment.title
                                              )}
                                              alt="file"
                                              className="me-2"
                                            />
                                            <div>
                                              <h6 title={attachment.title}>
                                                {truncateFileName(
                                                  attachment.title,
                                                  25
                                                )}
                                              </h6>
                                            </div>
                                          </div>
                                          <Link
                                            to="#"
                                            className="text-danger"
                                            onClick={() => {
                                              setAttachmentsToDelete((prev) => [
                                                ...prev,
                                                attachment.document_attachment_id!,
                                              ]);
                                              setDocumentAttachmentsList(
                                                (prev) => ({
                                                  ...prev,
                                                  [document.document_id!]: prev[
                                                    document.document_id!
                                                  ].filter(
                                                    (att) =>
                                                      att.document_attachment_id !==
                                                      attachment.document_attachment_id
                                                  ),
                                                })
                                              );
                                            }}
                                          >
                                            <i className="ti ti-trash-x" />
                                          </Link>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                {/* Add New Attachments */}
                                <AttachmentComponent
                                  attachments={editDocumentAttachments}
                                  allowUpload={true}
                                  maxFiles={5}
                                  maxFileSize={10 * 1024 * 1024}
                                  acceptedFileTypes={[".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx", ".xls", ".xlsx"]}
                                  onFileUpload={(files) => setEditDocumentAttachments(prev => [...prev, ...files])}
                                  onFileRemove={(index) => {
                                    setEditDocumentAttachments((prev) =>
                                      prev.filter((_, i) => i !== index)
                                    );
                                  }}
                                  variant="compact"
                                />
                              </div>
                            </div>
                            <div className="d-flex gap-2 mt-2">
                              <button
                                className="btn btn-sm btn-primary"
                                disabled={updatingDocument}
                                onClick={async () => {
                                  try {
                                    setUpdatingDocument(true);
                                    const updateData = {
                                      ...editDocumentData,
                                      assigned_user_id:
                                        editDocumentData.assigned_user_id ||
                                        undefined,
                                    };

                                    for (const attachmentId of attachmentsToDelete) {
                                      try {
                                        await DocumentService.deleteDocumentAttachment(
                                          attachmentId
                                        );
                                      } catch (error) {
                                        console.error(
                                          `Error deleting attachment ${attachmentId}:`,
                                          error
                                        );
                                      }
                                    }

                                    await DocumentService.updateDocument(
                                      document.document_id!,
                                      updateData,
                                      currentTenantId,
                                      editDocumentAttachments.length > 0
                                        ? editDocumentAttachments
                                        : undefined
                                    );

                                    setEditingDocument(null);
                                    setEditDocumentAttachments([]);
                                    setAttachmentsToDelete([]);
                                    loadDocuments();
                                    showSuccessAlert(
                                      "Document updated successfully"
                                    );

                                    if (onActivityUpdate) {
                                      onActivityUpdate();
                                    }
                                  } catch (error) {
                                    console.error(
                                      "Error updating document:",
                                      error
                                    );
                                    showErrorAlert("Failed to update document");
                                  } finally {
                                    setUpdatingDocument(false);
                                  }
                                }}
                              >
                                {updatingDocument ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" />
                                    Updating...
                                  </>
                                ) : (
                                  "Save"
                                )}
                              </button>
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                  setEditingDocument(null);
                                  setEditDocumentAttachments([]);
                                  setAttachmentsToDelete([]);
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="row align-items-start">
                            <div className="col-md-8">
                              <div className="mb-3">
                                <div className="d-flex align-items-center mb-2">
                                  <img
                                    src={document.created_by.avatar_url || male}
                                    alt="Profile Image"
                                    className="me-2"
                                    style={{
                                      width: "40px",
                                      height: "40px",
                                      borderRadius: "50%",
                                    }}
                                  />
                                  <h4 className="fw-medium mb-0">
                                    {document.title} <br />
                                    <small
                                      className="text-muted mb-0"
                                      style={{ fontSize: "10px" }}
                                    >
                                      <span style={{ fontWeight: "bold" }}>
                                        {document.created_by.full_name}{" "}
                                      </span>{" "}
                                      at{" "}
                                      <span style={{ fontWeight: "bold" }}>
                                        {document.created_datetime
                                          ? formatDateTime(
                                              document.created_datetime
                                            )
                                          : "Just now"}
                                      </span>
                                    </small>
                                  </h4>
                                </div>
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: document.content,
                                  }}
                                />

                                {/* Document Attachments */}
                                {documentAttachmentsList[
                                  document.document_id!
                                ] &&
                                  documentAttachmentsList[document.document_id!]
                                    .length > 0 && (
                                    <div
                                      className="file-attach mt-3"
                                      style={{
                                        width: "140%",
                                      }}
                                    >
                                      <h6 className="fw-medium mb-2">
                                        Attachments
                                      </h6>
                                      <div className="row">
                                        {documentAttachmentsList[
                                          document.document_id!
                                        ].map((attachment) => (
                                          <div
                                            key={
                                              attachment.document_attachment_id
                                            }
                                            className="col-md-4 mb-2"
                                          >
                                            <div className="attach-file-list d-flex align-items-center border rounded p-2">
                                              <div className="attach-img me-2">
                                                <ImageWithBasePath
                                                  src={getAttachmentIcon(
                                                    attachment.title
                                                  )}
                                                  alt="icon"
                                                />
                                              </div>
                                              <div className="attach-content flex-fill">
                                                <h6
                                                  className="mb-1"
                                                  title={attachment.title}
                                                >
                                                  {truncateFileName(
                                                    attachment.title,
                                                    20
                                                  )}
                                                </h6>
                                                <p className="mb-0 text-muted small">
                                                  {attachment.size_in_kb} KB
                                                </p>
                                              </div>
                                              <div className="attach-action">
                                                <button
                                                  type="button"
                                                  className="btn btn-sm btn-outline-primary"
                                                  disabled={downloadingAttachment === attachment.document_attachment_id}
                                                  onClick={() =>
                                                    downloadAttachment(
                                                      attachment.attachment_url,
                                                      attachment.title,
                                                      attachment.document_attachment_id!
                                                    )
                                                  }
                                                >
                                                  {downloadingAttachment === attachment.document_attachment_id ? (
                                                    <span className="spinner-border spinner-border-sm" />
                                                  ) : (
                                                    <i className="ti ti-arrow-down" />
                                                  )}
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                <div className="d-flex align-items-center mt-2">
                                  <span className="avatar avatar-md me-2 flex-shrink-0">
                                    <img
                                      src={
                                        document.assigned_user?.avatar_url ||
                                        male
                                      }
                                      alt="img"
                                      className="rounded-circle"
                                    />
                                  </span>
                                  {document.assigned_user && (
                                    <div className="ms-2">
                                      <span
                                        className="fs-12"
                                        style={{
                                          fontWeight: "bold",
                                        }}
                                      >
                                        Assigned User
                                      </span>
                                      <p className="mb-0">
                                        {document.assigned_user.full_name}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="col-md-4 text-md-end d-flex justify-content-end">
                              <div className="mb-3 d-inline-flex align-items-center">
                                <div style={{ fontSize: "15px" }}>
                                  {getStatusBadge(document.document_type)}
                                </div>
                                <div className="dropdown">
                                  <Link
                                    to="#"
                                    className="p-0 btn btn-icon btn-sm d-flex align-items-center justify-content-center"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                  >
                                    <i className="ti ti-dots-vertical" />
                                  </Link>
                                  <div className="dropdown-menu dropdown-menu-right">
                                    <Link
                                      className="dropdown-item"
                                      to="#"
                                      onClick={() => {
                                        setEditingDocument(
                                          document.document_id!
                                        );
                                        setEditDocumentData({
                                          title: document.title,
                                          content: document.content,
                                          document_type: document.document_type,
                                          assigned_user_id:
                                            document.assigned_user?.user_id ||
                                            null,
                                        });
                                      }}
                                    >
                                      <i className="ti ti-edit text-blue me-1" />
                                      Edit
                                    </Link>
                                    <Link
                                      className="dropdown-item"
                                      to="#"
                                      onClick={() =>
                                        handleDeleteDocument(
                                          document.document_id!
                                        )
                                      }
                                    >
                                      <i className="ti ti-trash text-danger me-1" />
                                      Delete
                                    </Link>
                                    <Link
                                      className="dropdown-item"
                                      to="#"
                                      onClick={() =>
                                        downloadDocumentAsZip(document)
                                      }
                                    >
                                      {downloadingDocument ===
                                      document.document_id ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm text-info me-1" />
                                          Downloading...
                                        </>
                                      ) : (
                                        <>
                                          <i className="ti ti-arrow-down text-info me-1" />
                                          Download
                                        </>
                                      )}
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Add Document Modal */}
      {showAddDocumentModal && (
        <div
          className="modal fade show d-block"
          id="new_file"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          tabIndex={-1}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h4 className="modal-title">Create New Document</h4>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowAddDocumentModal(false);
                    setNewDocument({
                      title: "",
                      content: "",
                      document_type: "PDF",
                      assigned_user_id: null,
                    });
                    setDocumentAttachments([]);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Title <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={newDocument.title ?? ""}
                      onChange={(e) =>
                        setNewDocument({
                          ...newDocument,
                          title: e.target.value,
                        })
                      }
                      placeholder="Enter document title"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">
                      Document Type <span className="text-danger">*</span>
                    </label>
                    <Select
                      className="select"
                      options={getDocumentTypeOptions()}
                      value={getDocumentTypeOptions().find(
                        (option) => option.value === newDocument.document_type
                      )}
                      onChange={(selected) =>
                        setNewDocument({
                          ...newDocument,
                          document_type: selected?.value || "PDF",
                        })
                      }
                      classNamePrefix="react-select"
                    />
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">Assigned User</label>
                    <Select
                      className="select"
                      options={tenantUsers.map((user) => ({
                        value: user.user_id,
                        label: `${user.user_first_name} ${user.user_last_name} (${user.email})`,
                      }))}
                      value={tenantUsers
                        .map((user) => ({
                          value: user.user_id,
                          label: `${user.user_first_name} ${user.user_last_name} (${user.email})`,
                        }))
                        .find(
                          (option) =>
                            option.value === newDocument.assigned_user_id
                        )}
                      onChange={(selected) =>
                        setNewDocument({
                          ...newDocument,
                          assigned_user_id: selected?.value || null,
                        })
                      }
                      classNamePrefix="react-select"
                      placeholder="Select assigned user (optional)"
                      isClearable
                    />
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">
                      Content <span className="text-danger">*</span>
                    </label>
                    <DefaultEditor
                      value={newDocument.content ?? ""}
                      onChange={(e) =>
                        setNewDocument({
                          ...newDocument,
                          content: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="col-md-12 mb-3">
                    <label className="form-label">Attachments</label>
                    <AttachmentComponent
                      attachments={documentAttachments}
                      allowUpload={true}
                      maxFiles={5}
                      maxFileSize={10 * 1024 * 1024}
                      acceptedFileTypes={[".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx", ".xls", ".xlsx"]}
                      onFileUpload={(files) => setDocumentAttachments(prev => [...prev, ...files])}
                      onFileRemove={(index) => removeAttachment(index)}
                      variant="compact"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  onClick={() => {
                    setShowAddDocumentModal(false);
                    setNewDocument({
                      title: "",
                      content: "",
                      document_type: "PDF",
                      assigned_user_id: null,
                    });
                    setDocumentAttachments([]);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={savingDocument}
                  onClick={handleAddDocument}
                >
                  {savingDocument ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Creating...
                    </>
                  ) : (
                    "Create Document"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Files;
